import { Classes } from "@blueprintjs/core";
import React from "react";
import styled, { css } from "styled-components";

import { Flex } from "./Flex";
import {
  BUTTON_OVERRIDE,
  HexAnchorButton,
  HexAnchorButtonProps,
} from "./HexButton";
import { Txt, TxtProps } from "./Txt";

// ----- Root -----

export interface HexNavBarBreadcrumbRootProps extends React.ComponentPropsWithoutRef<
  typeof Flex
> {}

const Root = React.memo(
  ({
    ref,
    ...props
  }: HexNavBarBreadcrumbRootProps & { ref?: React.Ref<HTMLDivElement> }) => {
    return (
      <Flex aria-label="breadcrumb" role="navigation" {...props} ref={ref} />
    );
  },
);

// ----- List -----

export interface HexNavBarBreadcrumbListProps extends React.ComponentPropsWithoutRef<
  typeof Flex
> {}

const List = React.memo(
  ({
    ref,
    ...props
  }: HexNavBarBreadcrumbListProps & { ref?: React.Ref<HTMLDivElement> }) => {
    return <Flex role="list" {...props} ref={ref} />;
  },
);

// ----- Item -----

export interface HexNavBarBreadcrumbItemProps extends React.ComponentPropsWithoutRef<
  typeof Flex
> {}

const Item = React.memo(
  ({
    ref,
    ...props
  }: HexNavBarBreadcrumbItemProps & { ref?: React.Ref<HTMLDivElement> }) => {
    return <Flex role="listitem" {...props} ref={ref} />;
  },
);

// ----- Link -----

export interface HexNavBarBreadcrumbLinkProps extends HexAnchorButtonProps {}

const Link = React.memo(
  ({
    minimal = true,
    ref,
    ...props
  }: HexNavBarBreadcrumbLinkProps & { ref?: React.Ref<HTMLAnchorElement> }) => {
    return <LinkAnchorButton minimal={minimal} {...props} ref={ref} />;
  },
);

const LinkAnchorButton = styled(HexAnchorButton)`
  &&&&&&${BUTTON_OVERRIDE} {
    min-width: 0;
    min-height: 26px;
    padding: 3px 4px;

    ${({ theme }) => css`
      @media (max-width: ${theme.mediaQuery.EXTRA_SMALL}px) {
        min-height: 21px;
      }
      @media (width > ${theme.subNavigationBreakpoint}px) {
        gap: 0;
      }
    `}

    .${Classes.BUTTON_TEXT} {
      display: flex;
      gap: 6px;
      align-items: center;
      color: ${({ theme }) => theme.fontColor.MUTED};
      min-width: 0;

      ${({ theme }) => css`
        @media (max-width: ${theme.mediaQuery.EXTRA_SMALL}px) {
          div {
            font-size: ${theme.fontSize.SMALL};
            line-height: ${theme.lineHeight.SMALL};
          }
        }
      `}

      @media (width <= ${({ theme }) => theme.subNavigationBreakpoint}px) {
        display: none;
      }
    }

    .${Classes.ICON} {
      @media (width > ${({ theme }) => theme.subNavigationBreakpoint}px) {
        display: none;
      }
    }
  }
`;

// ----- Page -----

export interface HexNavBarBreadcrumbPageProps extends React.ComponentPropsWithoutRef<
  typeof Flex
> {}

const Page = React.memo(
  ({
    ref,
    ...props
  }: HexNavBarBreadcrumbPageProps & { ref?: React.Ref<HTMLDivElement> }) => {
    return (
      <PageFlex
        aria-current="page"
        aria-disabled="true"
        role="link"
        {...props}
        ref={ref}
      />
    );
  },
);

const PageFlex = styled(Flex)`
  ${({ theme }) => css`
    @media (max-width: ${theme.mediaQuery.EXTRA_SMALL}px) {
      font-size: ${theme.fontSize.SMALL};
      line-height: ${theme.lineHeight.SMALL};
    }
  `}
`;

// ----- Separator -----

export interface HexNavBarBreadcrumbSeparatorProps extends React.ComponentPropsWithoutRef<
  typeof Flex
> {}

const Separator = React.memo(
  ({
    children,
    ref,
    ...rest
  }: HexNavBarBreadcrumbSeparatorProps & {
    ref?: React.Ref<HTMLDivElement>;
  }) => {
    return (
      <Flex aria-hidden="true" role="presentation" {...rest} ref={ref}>
        {children ?? <ForwardSlash />}
      </Flex>
    );
  },
);

// ----- Forward Slash -----

interface HexNavBarBreadcrumbForwardSlashProps extends TxtProps {}

const ForwardSlash_ = React.memo(
  ({
    children = <>/</>,
    fontColor = "muted",
    fontSize = "default",
    fontWeight = "normal",
    ref,
    ...rest
  }: HexNavBarBreadcrumbForwardSlashProps & {
    ref?: React.Ref<HTMLElement>;
  }) => {
    return (
      <ForwardSlashTxt
        ref={ref}
        fontColor={fontColor}
        fontSize={fontSize}
        fontWeight={fontWeight}
        {...rest}
      >
        {children}
      </ForwardSlashTxt>
    );
  },
);
const ForwardSlash = styled(ForwardSlash_)``;

const ForwardSlashTxt = styled(Txt)`
  width: 6px;
  line-height: 26px; /* one pixel below XL */
  opacity: 0.4;

  ${({ theme }) => css`
    @media (max-width: ${theme.mediaQuery.EXTRA_SMALL}px) {
      font-size: ${theme.fontSize.SMALL};
      line-height: ${theme.lineHeight.LARGE};
    }
  `}
`;

// ----- Export -----

/**
 * A breadcrumb component for use in the top navigation bar.
 *
 * @example
 * <HexNavBarBreadcrumb.Root>
 *   <HexNavBarBreadcrumb.List>
 *     // The encompassing object, like a page ("Data (Browser)") or a collection
 *     <HexNavBarBreadcrumb.Item>
 *       <HexNavBarBreadcrumb.Link>Data</HexNavBarBreadcrumb.Link>
 *     </HexNavBarBreadcrumb.Item>
 *     <HexNavBarBreadcrumb.Separator />
 *     // The specific object, like a project.
 *     <HexNavBarBreadcrumb.Item>
 *       <HexNavBarBreadcrumb.Page>Pizza Bytes</HexNavBarBreadcrumb.Page>
 *     </HexNavBarBreadcrumb.Item>
 *   </HexNavBarBreadcrumb.List>
 * </HexNavBarBreadcrumb.Root>
 */
export const HexNavBarBreadcrumb = Object.freeze({
  Root: Root,
  List: List,
  Item: Item,
  Link: Link,
  Page: Page,
  Separator: Separator,
  ForwardSlash: ForwardSlash,
});
